import { useEffect, useRef, useState } from "react";
import { getArtistMessages, sendArtistMessage } from "../../../api/messagesApi";
import Toast from "../../../components/toast";
import ToastContainer from "../../../components/toastContainer";
import PageLoader from "../../../components/pageLoader";
import { PaperClipIcon, PaperAirplaneIcon } from "@heroicons/react/outline";

const CommunicationTab = ({ artist }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);

  const bottomRef = useRef(null);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const data = await getArtistMessages(artist.id);
      const list = Array.isArray(data) ? data : data.results || [];

      const sorted = [...list].sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at),
      );

      setMessages(sorted);
    } catch (err) {
      setToast({
        type: "error",
        message: err?.response?.data?.detail || "Failed to load messages",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (artist?.id) {
      fetchMessages();
    }
  }, [artist?.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!text && !file) return;

    try {
      setSending(true);

      const formData = new FormData();
      if (text) formData.append("text", text);
      if (file) formData.append("attachment", file);

      const newMessage = await sendArtistMessage(artist.id, formData);

      setMessages((prev) =>
        [...prev, newMessage].sort(
          (a, b) => new Date(a.created_at) - new Date(b.created_at),
        ),
      );

      setText("");
      setFile(null);
    } catch (err) {
      setToast({
        type: "error",
        message: err?.response?.data?.detail || "Failed to send message",
      });
    } finally {
      setSending(false);
    }
  };

  if (loading) return <PageLoader />;

  return (
    <div className="flex flex-col h-[70vh] bg-slate-900/60 border border-white/10 rounded-xl">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-1">
        {messages.map((msg) => {
          const isMine = !msg.sent_from_artist;

          return (
            <div
              key={msg.id}
              className={`flex ${isMine ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[70%] rounded-lg px-4 py-2 text-sm
                  ${
                    isMine
                      ? "bg-pink-600 text-white"
                      : "bg-slate-800 text-slate-200"
                  }
                `}
              >

                {/* Text */}
                {msg.text && <p>{msg.text}</p>}

                {/* Attachment */}
                {msg.attachment && (
                  <a
                    href={msg.attachment}
                    target="_blank"
                    rel="noreferrer"
                    className="block mt-2 text-xs underline"
                  >
                    View attachment
                  </a>
                )}

                {/* Time */}
                <p className="text-[8px] opacity-60 text-right">
                  {formatTime(msg.created_at)}
                </p>
              </div>
            </div>
          );
        })}

        <div ref={bottomRef} />
      </div>

      {file && (
        <div className="px-3 pb-2 flex items-center gap-2 text-xs text-slate-300">
          <span className="bg-slate-800 px-2 py-1 rounded">📎 {file.name}</span>
          <button
            onClick={() => setFile(null)}
            className="text-red-400 hover:text-red-500"
          >
            Remove
          </button>
        </div>
      )}

      {/* Input */}
      <div className="border-t border-white/10 p-3 flex items-center gap-2">
        {/* Attachment */}
        <label className="cursor-pointer text-slate-400 hover:text-white">
          <PaperClipIcon className="w-5 h-5" />
          <input
            type="file"
            className="hidden"
            onChange={(e) => setFile(e.target.files[0])}
          />
        </label>

        {/* Text input */}
        <input
          type="text"
          placeholder="Type a message..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="flex-1 bg-[#131C31] border border-white/10 rounded-lg px-4 py-2 text-sm text-white
            focus:outline-none focus:ring-2 focus:ring-pink-500/40"
        />

        {/* Send */}
        <button
          onClick={handleSend}
          disabled={sending}
          className="bg-pink-600 hover:bg-pink-700 disabled:bg-pink-400 text-white p-2 rounded-lg"
        >
          <PaperAirplaneIcon className="w-4 h-4" />
        </button>
      </div>

      <ToastContainer>
        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}
      </ToastContainer>
    </div>
  );
};

const formatTime = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default CommunicationTab;
